import {
	Box,
	createStyles,
	Divider,
	Group,
	Stack,
	TextInput,
} from '@mantine/core';
import type { BiMetricConfig } from '@repo/api-codegen';
import { UserRole } from '@repo/common/enums/UserRole';
import { IconButton, SegmentedControl } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { memo, Suspense, useCallback, useMemo, useState } from 'react';
import {
	PageLayoutContent,
	PageLayoutContentWrapper,
	PageLayoutOuterWrapper,
	PageLayoutWrapper,
} from '../../components/PageLayout';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { BiMetricNavbarController } from './BiMetricNavbarController.tsx';

import { space } from '@repo/theme/primitives/space.ts';
import { typography } from '@repo/theme/primitives/typography.ts';
import { useMount } from 'ahooks';
import { Helmet } from 'react-helmet';
import { RightSidebarWrapperController } from '../../components/RightSidebar/RightSidebarWrapper';
import {
	rightSidebarStore,
	RightSidebarStore,
} from '../../components/RightSidebar/store';
import { NavBarRightSideWrapper } from '../../components/SecodaAppShell/SecodaNavBar/NavBarRightSideWrapper.tsx';
import { NavBarWrapper } from '../../components/SecodaAppShell/SecodaNavBar/NavBarWrapper.tsx';
import { AiSideChatForObjectController } from '../AiSideChatForObject/AiSideChatForObjectController.tsx';
import { BiMetricChartRender } from './components/BiMetricChartRender.tsx';
import { BiMetricEditor } from './components/BiMetricEditor.tsx';
import { BiMetricResultsRender } from './components/BiMetricResultsRender.tsx';
import { BIMetricPageSkeleton } from './components/BiMetricSkeleton.tsx';
import { BiMetricSqlRender } from './components/BiMetricSqlRender.tsx';
import { FloatingSaveDiscardBar } from './components/FloatingSaveDiscardBar';
import { useSuspenseBiMetricStore } from './hooks/useSuspenseBiMetricStore.ts';
import type { BIMetricStore } from './store.ts';

interface BIMetricControllerProps {
	metricId: string;
	mode?: 'canvas' | 'page' | 'modal';
	closeCanvas?: () => void;
	navigateToDashboardInCanvas?: (id: string) => void;
	startingPointEntityIdentifier?: string;
	modalFooter?: (store: BIMetricStore) => React.ReactNode;
	initialTab?: 'chart' | 'results' | 'sql';
	isCreation?: boolean;
}

interface BiMetricContentProps {
	store: ReturnType<typeof useSuspenseBiMetricStore>['store'];
	initialTab?: 'chart' | 'results' | 'sql';
	isCreation?: boolean;
}

const useStyles = createStyles((theme) => ({
	titleTextInput: {
		fontSize: typography.text['xl'],
		fontWeight: typography.weight.bold,
		padding: 0,
		overflowY: 'hidden',

		'&:focus': {
			boxShadow: 'none',
			borderColor: 'transparent',
			backgroundColor: 'transparent',
		},

		'&:disabled': {
			opacity: 1,
			backgroundColor: theme.other.getColor('fill/transparent/default'),
			color: theme.other.getColor('text/primary/default'),
			cursor: 'text',
		},
	},
	descriptionTextInput: {
		fontSize: typography.text['md'],
		padding: 0,
		overflowY: 'hidden',

		'&:focus': {
			boxShadow: 'none',
			borderColor: 'transparent',
			backgroundColor: 'transparent',
		},

		'&:disabled': {
			opacity: 1,
			backgroundColor: theme.other.getColor('fill/transparent/default'),
			color: theme.other.getColor('text/primary/default'),
			cursor: 'text',
		},
	},
	modalContentGroup: {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
	},
	modalLeftContent: {
		flex: 1,
	},
	modalRightContent: {
		minHeight: '100%',
	},
	modalRightSidebarContent: {
		borderLeft: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		flex: 1,
	},
	borderLeft: {
		borderLeft: `1px solid ${theme.other.getColor('border/secondary/default')}`,
	},
}));

const BiMetricContent = observer(
	({
		store,
		initialTab = 'chart',
		isCreation = false,
		onCreationAiClick,
		readOnly = false,
	}: BiMetricContentProps & {
		onCreationAiClick?: () => void;
		readOnly?: boolean;
	}) => {
		const [selectedTab, setSelectedTab] = useState(initialTab);
		const { classes } = useStyles();

		return (
			<Stack w="100%" spacing="md">
				<Stack spacing={0}>
					<TextInput
						classNames={{
							root: classes.titleTextInput,
							input: classes.titleTextInput,
						}}
						value={store.metricVersion.name}
						variant="unstyled"
						placeholder="New metric..."
						onChange={(event) => {
							store.setName(event.currentTarget.value);
						}}
					/>
					<TextInput
						classNames={{
							root: classes.descriptionTextInput,
							input: classes.descriptionTextInput,
						}}
						variant="unstyled"
						placeholder="Add description..."
						onChange={(event) => {
							store.setDescription(event.currentTarget.value);
						}}
						color="text/secondary/default"
						value={store.metricVersion.description ?? ''}
					/>
				</Stack>
				<Group>
					<SegmentedControl
						data={[
							{ label: 'Chart', value: 'chart' },
							{ label: 'Results', value: 'results' },
							{ label: 'SQL', value: 'sql' },
						]}
						value={selectedTab}
						onChange={(value) =>
							setSelectedTab(value as 'chart' | 'results' | 'sql')
						}
						size="sm"
					/>
				</Group>

				{selectedTab === 'chart' && (
					<BiMetricChartRender
						metricVersion={store.metricVersion}
						mode="view_only"
						shadow
						isCreation={isCreation}
						onCreationSqlClick={() => setSelectedTab('sql')}
						onCreationAiClick={onCreationAiClick}
					/>
				)}
				{selectedTab === 'results' && (
					<BiMetricResultsRender results={store.metricVersion.results} />
				)}
				{selectedTab === 'sql' && (
					<BiMetricSqlRender
						key={store.lastResetPlaygroundAt}
						metricVersion={store.metricVersion}
						onExecutionEnd={store.onNewSqlExecution}
						readOnly={readOnly}
					/>
				)}
			</Stack>
		);
	}
);

const BIMetricPageControllerInternal = observer(
	({
		metricId,
		mode = 'page',
		modalFooter,
		closeCanvas,
		navigateToDashboardInCanvas,
		startingPointEntityIdentifier: startingPointEntityIdentifierProp,
		initialTab,
		isCreation = false,
	}: BIMetricControllerProps) => {
		const { store } = useSuspenseBiMetricStore(metricId);
		const { classes } = useStyles();
		const [modelRightSideBarMode, setModelRightSideBarMode] = useState<
			'ai' | 'info'
		>('ai');

		const { hasPermission: hasManagePermission } = useCheckIAMPermission({
			v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
			v2Permission: 'Dashboards.Manage',
		});

		const handleChartConfigUpdate = useCallback(
			(config: BiMetricConfig['chart_config']) => {
				store.setChartConfig(config);
			},
			[store]
		);

		const rightSidebarStoreForMode = useMemo(() => {
			if (mode === 'page') {
				// Use the global one
				return rightSidebarStore;
			}

			return new RightSidebarStore();
		}, [mode]);

		const startingPointEntityIdentifier =
			startingPointEntityIdentifierProp ?? `bi_metric:${metricId}`;

		// Track metric viewed on mount
		useMount(() => {
			store.trackMetricViewed();
		});

		const selectedSidebarType =
			rightSidebarStoreForMode.mode === 'comment'
				? 'ai'
				: rightSidebarStoreForMode.mode;

		// TODO(nhanthai-dashboards): If user modify the chart manually, then ask AI to continue modification
		// should commit latest state to server before creating AI chart so that it is up to date

		if (mode === 'modal') {
			return (
				<Stack spacing={0} h="100%">
					<Group
						spacing={0}
						noWrap
						align="stretch"
						className={classes.modalContentGroup}
					>
						<Stack className={classes.modalLeftContent} p={space[6]}>
							<BiMetricContent
								store={store}
								initialTab={initialTab}
								isCreation={isCreation}
								onCreationAiClick={() => setModelRightSideBarMode('ai')}
								readOnly={!hasManagePermission}
							/>
						</Stack>
						{hasManagePermission && (
							<Stack
								w={space[90]}
								spacing={0}
								p={0}
								className={classes.modalRightContent}
							>
								<Box className={classes.borderLeft}>
									<NavBarWrapper>
										<Group />
										<NavBarRightSideWrapper>
											<IconButton
												iconName="sparkles"
												highlight={modelRightSideBarMode === 'ai'}
												variant="tertiary"
												tooltip="Show AI sidebar"
												onClick={() => {
													setModelRightSideBarMode('ai');
												}}
											/>
											<IconButton
												iconName="adjustmentsHorizontal"
												highlight={modelRightSideBarMode === 'info'}
												variant="tertiary"
												tooltip="Edit metric"
												onClick={() => {
													setModelRightSideBarMode('info');
												}}
											/>
										</NavBarRightSideWrapper>
									</NavBarWrapper>
								</Box>
								<Stack className={classes.modalRightSidebarContent}>
									{modelRightSideBarMode === 'ai' ? (
										<AiSideChatForObjectController
											startingPointEntityIdentifier={
												startingPointEntityIdentifier
											}
										/>
									) : modelRightSideBarMode === 'info' ? (
										<BiMetricEditor
											chartConfig={store.metricVersion.config.chart_config}
											results={store.metricVersion.results}
											onUpdate={handleChartConfigUpdate}
										/>
									) : null}
								</Stack>
							</Stack>
						)}
					</Group>
					<Stack spacing={0}>
						<Divider />
						{modalFooter && modalFooter(store)}
					</Stack>
				</Stack>
			);
		}

		return (
			<PageLayoutOuterWrapper>
				<PageLayoutWrapper name="bi-metric-page" key={metricId}>
					<Helmet>
						<title>{store.metricVersion.name}</title>
					</Helmet>

					<FloatingSaveDiscardBar
						hasChanges={store.hasChanges}
						isUpdating={store.isUpdating}
						onSave={store.saveChanges}
						onDiscard={store.discardChanges}
					/>
					<PageLayoutContentWrapper name="bi-metric-page">
						<BiMetricNavbarController
							mode={mode}
							store={store}
							rightSidebarStore={rightSidebarStoreForMode}
							closeCanvas={closeCanvas}
							navigateToDashboardInCanvas={navigateToDashboardInCanvas}
							selectedSidebarType={selectedSidebarType}
							setSelectedSidebarType={rightSidebarStoreForMode.setMode}
						/>
						<PageLayoutContent>
							<BiMetricContent
								store={store}
								initialTab={initialTab}
								isCreation={isCreation}
								readOnly={!hasManagePermission}
							/>
						</PageLayoutContent>
					</PageLayoutContentWrapper>
					{hasManagePermission && (
						<RightSidebarWrapperController store={rightSidebarStoreForMode}>
							{rightSidebarStoreForMode.mode === 'ai' ? (
								<AiSideChatForObjectController
									startingPointEntityIdentifier={startingPointEntityIdentifier}
								/>
							) : rightSidebarStoreForMode.mode === 'info' ? (
								<BiMetricEditor
									chartConfig={store.metricVersion.config.chart_config}
									results={store.metricVersion.results}
									onUpdate={handleChartConfigUpdate}
								/>
							) : null}
						</RightSidebarWrapperController>
					)}
				</PageLayoutWrapper>
			</PageLayoutOuterWrapper>
		);
	}
);

export const BiMetricPageController = memo((props: BIMetricControllerProps) => (
	<Suspense fallback={<BIMetricPageSkeleton />}>
		<BIMetricPageControllerInternal {...props} />
	</Suspense>
));
